/**
 * Boomfection Landing — Daily Reward Streak Widget
 * Shows 7-day reward cycle with claim functionality
 * @copyright reDim GmbH | @link https://www.redim.de | @contact team@redim.de
 */

import { t } from '/js/i18n.js';
import { Navigation } from '/js/ui/Navigation.js';

/**
 * Renders the daily reward widget into the given container
 * @param {HTMLElement} container
 */
export async function renderDailyRewardWidget(container) {
    if (!container) return;

    const user = Navigation.getUser();
    if (!user) {
        container.classList.add('hidden');
        return;
    }

    container.classList.remove('hidden');
    container.textContent = '';

    const wrapper = document.createElement('div');
    wrapper.className = 'daily-reward-widget';

    // Title
    const title = document.createElement('div');
    title.className = 'daily-reward-title';
    title.textContent = t('dailyReward.title');
    wrapper.appendChild(title);

    // Loading state
    const loadingEl = document.createElement('div');
    loadingEl.className = 'daily-reward-loading';
    loadingEl.textContent = t('common.loading');
    wrapper.appendChild(loadingEl);
    container.appendChild(wrapper);

    try {
        const res = await Navigation.authFetch('/api/v1/daily-login/status');
        if (!res) {
            loadingEl.textContent = t('common.error');
            return;
        }
        if (res.status === 401) {
            container.classList.add('hidden');
            return;
        }
        if (!res.ok) {
            loadingEl.textContent = t('common.error');
            return;
        }

        const data = await res.json();
        loadingEl.remove();

        const streak = data.streak || 0;
        const claimedToday = data.claimedToday || false;
        const rewards = data.rewards || [];
        const dayInCycle = streak > 0 ? ((streak - 1) % 7) + 1 : 0;

        // Days row
        const daysRow = document.createElement('div');
        daysRow.className = 'daily-reward-days';

        rewards.forEach((reward, index) => {
            const dayNum = index + 1;
            const circle = document.createElement('div');
            circle.className = 'daily-reward-day';

            const isClaimed = dayInCycle > dayNum || (dayInCycle === dayNum && claimedToday);
            const isToday = dayInCycle === dayNum && !claimedToday;
            const isTodayClaimed = dayInCycle === dayNum && claimedToday;
            const isNext = !claimedToday && dayNum === (dayInCycle > 0 ? dayInCycle : 1);

            if (isClaimed || isTodayClaimed) {
                circle.classList.add('claimed');
            } else if (isToday || isNext) {
                circle.classList.add('today');
            } else {
                circle.classList.add('future');
            }

            const dayLabel = document.createElement('span');
            dayLabel.className = 'daily-reward-day-num';
            dayLabel.textContent = dayNum;
            circle.appendChild(dayLabel);

            const xpLabel = document.createElement('span');
            xpLabel.className = 'daily-reward-day-xp';
            xpLabel.textContent = t('dailyReward.xpAmount', { amount: reward.amount });
            circle.appendChild(xpLabel);

            if (isClaimed || isTodayClaimed) {
                const check = document.createElement('span');
                check.className = 'daily-reward-check';
                check.textContent = '\u2713';
                circle.appendChild(check);
            }

            daysRow.appendChild(circle);
        });

        wrapper.appendChild(daysRow);

        // Streak info
        const streakInfo = document.createElement('div');
        streakInfo.className = 'daily-reward-streak';
        streakInfo.textContent = t('dailyReward.streak', { count: streak });
        wrapper.appendChild(streakInfo);

        // Claim button (only if not claimed today)
        if (!claimedToday) {
            const claimBtn = document.createElement('button');
            claimBtn.className = 'daily-reward-claim-btn';
            claimBtn.textContent = t('dailyReward.claim');

            claimBtn.addEventListener('click', async () => {
                claimBtn.disabled = true;
                claimBtn.textContent = t('common.loading');

                try {
                    const claimRes = await Navigation.authFetch('/api/v1/daily-login', {
                        method: 'POST'
                    });

                    if (!claimRes || !claimRes.ok) {
                        claimBtn.textContent = t('common.error');
                        return;
                    }

                    const result = await claimRes.json();

                    if (result.success && !result.alreadyClaimed) {
                        // Animate the XP gain, then re-render after animation completes
                        const xpNotif = document.createElement('div');
                        xpNotif.className = 'daily-reward-xp-notif';
                        xpNotif.textContent = t('game.xpGained', { xp: result.reward?.amount || 0 });
                        wrapper.appendChild(xpNotif);
                        setTimeout(() => {
                            xpNotif.remove();
                            // Re-render the widget after animation plays
                            renderDailyRewardWidget(container);
                        }, 2000);
                    } else {
                        claimBtn.textContent = t('dailyReward.alreadyClaimed');
                        claimBtn.disabled = true;
                    }
                } catch (err) {
                    console.warn('[LandingDailyReward] Claim request failed:', err);
                    claimBtn.textContent = t('common.error');
                    claimBtn.disabled = false;
                }
            });

            wrapper.appendChild(claimBtn);
        } else {
            const claimedMsg = document.createElement('div');
            claimedMsg.className = 'daily-reward-claimed-msg';
            claimedMsg.textContent = t('dailyReward.alreadyClaimed');
            wrapper.appendChild(claimedMsg);
        }
    } catch (err) {
        console.warn('[LandingDailyReward] Failed to render widget:', err);
        loadingEl.textContent = t('common.error');
    }
}
